'use client'

import { MediaButton, MediaInput, MediaModal, MediaPopover, MediaSelect } from './ui'
import Plasma from './components/plasma/Plasma'
import reshootBackground from '../public/wait1.png'
import explainerBackground from '../public/wait2.png'
import montageBackground from '../public/wait3.png'
import frameAnalysisBackground from '../public/wait4.png'

import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ChangeEvent,
  type ReactNode,
} from 'react'
import type { NavigationPort } from '@flowx/module-contracts'
import { MediaUserApiError, type MediaUserApi } from './media-user-api'
import {
  MEDIA_MODALITIES,
  MEDIA_PROJECT_SCOPES,
  isPrivateMediaResourceAccessURL,
  type MediaModelCatalogItem,
  type MediaModality,
  type MediaRecycleBinProject,
  type MediaProjectScope,
  type MediaProjectSummary,
  type MediaUploadTicket,
} from './media-user-contract'
import { toMediaSafeUserError, type MediaSafeUserError } from './media-flow-domain'
import { selectProjectCoverResource, type MediaProjectCover } from './media-project-cover'
import type { MediaActionPermission, MediaActionPolicy } from './media-action-policy'
import type { MediaTelemetry } from './media-telemetry'
import { sha256MediaUploadFile, uploadMediaFileToTicket, validateMediaUploadFile } from './media-upload'

const SEARCH_DEBOUNCE_MS = 300
const PROJECT_PAGE_SIZE = 12
const PROJECT_COVER_CONCURRENCY = 3
const PROJECT_COVER_REUSE_SAFETY_MS = 30_000
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const PROJECT_COVER_ACCEPT = 'image/png,image/jpeg,image/gif,image/svg+xml,.png,.jpg,.jpeg,.gif,.svg'

// An uploaded cover travels the ordinary project-bound signed-upload
// pipeline, so the OSS object inherits the project lifecycle: permanent until
// the project is purged from the recycle bin. The upload port seam exists for
// deterministic controller tests, mirroring the workspace library.
export interface MediaProjectCoverUploadPort {
  readonly sha256: (file: File) => Promise<string>
  readonly transfer: (ticket: MediaUploadTicket, file: File) => Promise<void>
}

const browserCoverUploadPort: MediaProjectCoverUploadPort = {
  sha256: (file) => sha256MediaUploadFile(file),
  transfer: (ticket, file) => uploadMediaFileToTicket(ticket, file),
}

export type MediaProjectHomeSection = 'home' | 'projects' | 'recycle' | 'skills'
export type MediaProjectOwnerFilter = 'mine' | 'all'

export interface MediaProjectHomeFilters {
  readonly status: 'active' | 'archived'
  readonly scope?: MediaProjectScope
  readonly owner: MediaProjectOwnerFilter
}

export interface MediaProjectHomeSnapshot {
  readonly status: 'idle' | 'loading' | 'ready' | 'error'
  readonly section: MediaProjectHomeSection
  readonly openedSections: readonly MediaProjectHomeSection[]
  readonly items: readonly MediaProjectSummary[]
  // Decorative card previews keyed by project UUID. Sourced from each
  // project's canvas (image output first, otherwise a video first frame);
  // entries are best-effort and may be absent while loading or on failure.
  readonly covers: Readonly<Record<string, MediaProjectCover | undefined>>
  // This deliberately narrower DTO is always sourced from the service's
  // ACL-filtered lifecycle list. It cannot carry regular project, canvas,
  // task, creator, resource or storage metadata.
  readonly recycleItems: readonly MediaRecycleBinProject[]
  readonly purgeRequestedProjectUuids: readonly string[]
  readonly page: number
  readonly pageSize: number
  readonly total: number
  readonly searchInput: string
  readonly query: string
  readonly filters: MediaProjectHomeFilters
  readonly skillsEnabled: boolean
  readonly modelStatus: 'idle' | 'loading' | 'ready' | 'error'
  readonly models: readonly MediaModelCatalogItem[]
  readonly modelError?: MediaSafeUserError
  readonly action?: 'create' | 'rename' | 'cover' | 'duplicate' | 'archive' | 'restore' | 'delete' | 'expand' | 'purge'
  readonly error?: MediaSafeUserError
  readonly actionError?: MediaSafeUserError
}

export interface MediaProjectHomeScheduler {
  readonly setTimeout: (run: () => void, delay: number) => unknown
  readonly clearTimeout: (handle: unknown) => void
}

export interface CreateMediaProjectHomeControllerOptions {
  readonly api: MediaUserApi
  readonly navigation: NavigationPort
  readonly scheduler?: MediaProjectHomeScheduler
  readonly skillsEnabled?: boolean
  readonly policy: MediaActionPolicy
  readonly telemetry?: MediaTelemetry
  readonly coverUploadPort?: MediaProjectCoverUploadPort
}

export interface MediaProjectHomeController {
  getSnapshot(): MediaProjectHomeSnapshot
  can(permission: MediaActionPermission): boolean
  subscribe(listener: () => void): () => void
  start(): Promise<void>
  retry(): Promise<void>
  setSkillsEnabled(enabled: boolean): void
  setSection(section: MediaProjectHomeSection): void
  closeSection(section: MediaProjectHomeSection): void
  setSearchInput(value: string): void
  setFilters(filters: Partial<MediaProjectHomeFilters>): Promise<void>
  setPage(page: number): Promise<void>
  openProject(projectUuid: string): void
  createProject(input: { readonly name: string; readonly scope: MediaProjectScope }): Promise<void>
  renameProject(projectUuid: string, name: string): Promise<void>
  updateProjectCover(projectUuid: string, file: File): Promise<void>
  duplicateProject(projectUuid: string, name: string): Promise<void>
  archiveProject(projectUuid: string): Promise<void>
  restoreProject(projectUuid: string): Promise<void>
  deleteProject(projectUuid: string): Promise<void>
  expandProjectScope(projectUuid: string): Promise<void>
  requestProjectPurge(projectUuid: string): Promise<void>
  dismissActionError(): void
  dispose(): void
}

const browserScheduler: MediaProjectHomeScheduler = {
  setTimeout: (run, delay) => globalThis.setTimeout(run, delay),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
}

function safeError(value: unknown): MediaSafeUserError {
  if (value instanceof MediaUserApiError) {
    return {
      code: value.code,
      message: value.safeMessage,
      ...(value.request_id ? { request_id: value.request_id } : {}),
    }
  }
  return toMediaSafeUserError(value)
}

function projectName(value: string): string {
  if (typeof value !== 'string') throw new MediaUserApiError(safeError(undefined))
  const normalized = value.trim()
  if (normalized.length === 0 || Array.from(normalized).length > 80) {
    throw new MediaUserApiError({ code: 'E7009', message: '请求参数有误，请检查后重试' })
  }
  return normalized
}

function projectUuid(value: unknown): string {
  if (typeof value !== 'string' || !UUID_PATTERN.test(value)) {
    throw new MediaUserApiError({ code: 'E7009', message: '请求参数有误，请检查后重试' })
  }
  return value.toLowerCase()
}

function adjacentProjectScope(scope: MediaProjectScope): MediaProjectScope | undefined {
  if (scope === 'owner') return 'group'
  if (scope === 'group') return 'team'
  return undefined
}

export function createMediaProjectHomeController({
  api,
  navigation,
  scheduler = browserScheduler,
  skillsEnabled = false,
  policy,
  telemetry,
  coverUploadPort = browserCoverUploadPort,
}: CreateMediaProjectHomeControllerOptions): MediaProjectHomeController {
  let snapshot: MediaProjectHomeSnapshot = {
    status: 'idle',
    section: 'home',
    openedSections: ['home'],
    items: [],
    covers: {},
    recycleItems: [],
    purgeRequestedProjectUuids: [],
    page: 1,
    pageSize: PROJECT_PAGE_SIZE,
    total: 0,
    searchInput: '',
    query: '',
    filters: { status: 'active', owner: 'all' },
    skillsEnabled,
    modelStatus: 'idle',
    models: [],
  }
  const listeners = new Set<() => void>()
  let searchHandle: unknown
  let requestGeneration = 0
  let modelGeneration = 0
  let started = false
  let disposed = false
  const projectCovers = new Map<string, { readonly cover: MediaProjectCover; readonly expiresAt: number }>()
  const projectCoverMisses = new Set<string>()
  const projectCoverFetches = new Map<string, Promise<void>>()

  const publish = (next: MediaProjectHomeSnapshot): void => {
    snapshot = next
    listeners.forEach((listener) => listener())
  }
  const patch = (next: Partial<MediaProjectHomeSnapshot>): void => publish({ ...snapshot, ...next })

  const listParameters = () => ({
    page: snapshot.page,
    page_size: snapshot.pageSize,
    status: snapshot.filters.status,
    ...(snapshot.query ? { query: snapshot.query } : {}),
    ...(snapshot.filters.scope ? { scope: snapshot.filters.scope } : {}),
    owner: snapshot.filters.owner,
  })

  const loadProjects = async (generation = ++requestGeneration): Promise<void> => {
    policy.require('media.project.view')
    patch({ status: 'loading', error: undefined })
    try {
      const response = await api.listMediaProjects(listParameters())
      if (disposed || generation !== requestGeneration) return
      publish({
        ...snapshot,
        status: 'ready',
        items: response.data.items,
        page: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
        error: undefined,
      })
      hydrateProjectCovers(response.data.items)
    } catch (error) {
      if (disposed || generation !== requestGeneration) return
      patch({ status: 'error', error: safeError(error) })
    }
  }

  const loadRecycleBin = async (generation = ++requestGeneration): Promise<void> => {
    policy.require('media.project.delete')
    patch({ status: 'loading', error: undefined })
    try {
      const response = await api.listMediaProjectRecycleBin({
        page: snapshot.page,
        page_size: snapshot.pageSize,
      })
      if (disposed || generation !== requestGeneration) return
      publish({
        ...snapshot,
        status: 'ready',
        recycleItems: response.data.items,
        purgeRequestedProjectUuids: snapshot.purgeRequestedProjectUuids
          .filter((projectUuid) => response.data.items.some((item) => item.uuid === projectUuid)),
        page: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
        error: undefined,
      })
    } catch (error) {
      if (disposed || generation !== requestGeneration) return
      patch({ status: 'error', error: safeError(error) })
    }
  }

  // Card covers are optional decoration assembled from the same ACL-scoped
  // canvas + capability endpoints the workspace uses. A user-uploaded cover
  // (cover_resource_uuid) always wins; otherwise the newest canvas image (or
  // video first frame) is the fallback. Failures are cached as misses so list
  // refreshes do not re-query unavailable projects.
  const loadProjectCover = (item: MediaProjectSummary): Promise<void> => {
    const request = (async (): Promise<void> => {
      try {
        if (item.cover_resource_uuid) {
          try {
            const uploaded = await api.getMediaResourceAccessURL(item.cover_resource_uuid)
            if (disposed) return
            const uploadedExpiry = Date.parse(uploaded.data.expires_at)
            if (isPrivateMediaResourceAccessURL(uploaded.data.access_url)
              && Number.isFinite(uploadedExpiry) && uploadedExpiry > Date.now()) {
              const cover: MediaProjectCover = { kind: 'image', url: uploaded.data.access_url }
              projectCovers.set(item.uuid, { cover, expiresAt: uploadedExpiry })
              patch({ covers: { ...snapshot.covers, [item.uuid]: cover } })
              return
            }
          } catch {
            // An uploaded-cover miss (e.g. the resource was removed) falls
            // through to the canvas-derived preview instead of hiding the card.
          }
        }
        const canvas = await api.getMediaCanvas(item.uuid)
        if (disposed) return
        const selection = selectProjectCoverResource(canvas.data)
        if (!selection) {
          projectCoverMisses.add(item.uuid)
          return
        }
        const access = await api.getMediaResourceAccessURL(selection.resourceUuid)
        if (disposed) return
        if (!isPrivateMediaResourceAccessURL(access.data.access_url)) {
          projectCoverMisses.add(item.uuid)
          return
        }
        const expiresAt = Date.parse(access.data.expires_at)
        if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
          projectCoverMisses.add(item.uuid)
          return
        }
        const cover: MediaProjectCover = { kind: selection.kind, url: access.data.access_url }
        projectCovers.set(item.uuid, { cover, expiresAt })
        patch({ covers: { ...snapshot.covers, [item.uuid]: cover } })
      } catch {
        projectCoverMisses.add(item.uuid)
      }
    })()
    projectCoverFetches.set(item.uuid, request)
    return request.finally(() => {
      if (projectCoverFetches.get(item.uuid) === request) projectCoverFetches.delete(item.uuid)
    })
  }

  const hydrateProjectCovers = (items: readonly MediaProjectSummary[]): void => {
    if (disposed) return
    const pending = items.filter((item) => {
      if (projectCoverFetches.has(item.uuid)) return false
      const cached = projectCovers.get(item.uuid)
      if (cached) {
        if (cached.expiresAt > Date.now() + PROJECT_COVER_REUSE_SAFETY_MS) return false
        projectCovers.delete(item.uuid)
      }
      return !projectCoverMisses.has(item.uuid)
    })
    let cursor = 0
    const worker = async (): Promise<void> => {
      while (cursor < pending.length && !disposed) {
        const item = pending[cursor]
        cursor += 1
        await loadProjectCover(item)
      }
    }
    for (let index = 0; index < Math.min(PROJECT_COVER_CONCURRENCY, pending.length); index += 1) void worker()
  }

  const refreshCurrentSection = (generation = ++requestGeneration): Promise<void> => (
    snapshot.section === 'recycle'
      ? loadRecycleBin(generation)
      : snapshot.section === 'projects'
        ? loadProjects(generation)
        : Promise.resolve()
  )

  const loadModels = async (generation = ++modelGeneration): Promise<void> => {
    patch({ modelStatus: 'loading', modelError: undefined })
    try {
      const response = await api.listMediaModels()
      if (disposed || generation !== modelGeneration) return
      patch({ modelStatus: 'ready', models: response.data.models, modelError: undefined })
    } catch (error) {
      if (disposed || generation !== modelGeneration) return
      patch({ modelStatus: 'error', models: [], modelError: safeError(error) })
    }
  }

  const mutate = async <T,>(
    action: NonNullable<MediaProjectHomeSnapshot['action']>,
    operation: () => Promise<T>,
    apply: (value: T) => void | Promise<void>,
  ): Promise<void> => {
    patch({ action, actionError: undefined })
    try {
      const value = await operation()
      if (disposed) return
      await apply(value)
      patch({ action: undefined, actionError: undefined })
    } catch (error) {
      const normalized = safeError(error)
      patch({ action: undefined, actionError: normalized })
      if (error instanceof MediaUserApiError) throw error
      throw new MediaUserApiError(normalized)
    }
  }

  const controller: MediaProjectHomeController = {
    getSnapshot: () => snapshot,
    can: (permission) => policy.can(permission),
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    start: async () => {
      if (disposed) {
        disposed = false
        started = false
      }
      if (started) return
      started = true
      await Promise.all([refreshCurrentSection(), loadModels()])
    },
    retry: () => {
      projectCoverMisses.clear()
      return refreshCurrentSection()
    },
    setSkillsEnabled: (enabled) => {
      if (snapshot.skillsEnabled === enabled) return
      const leavingSkills = !enabled && snapshot.section === 'skills'
      patch({ skillsEnabled: enabled,
        ...(!enabled ? { openedSections: snapshot.openedSections.filter((section) => section !== 'skills') } : {}),
        ...(leavingSkills ? { section: 'home', page: 1 } : {}),
      })
      if (leavingSkills) void refreshCurrentSection()
    },
    setSection: (section) => {
      if (section === 'skills' && !snapshot.skillsEnabled) return
      if (section === 'recycle' && !policy.can('media.project.delete')) return
      const generation = ++requestGeneration
      patch({
        section,
        openedSections: snapshot.openedSections.includes(section)
          ? snapshot.openedSections
          : [...snapshot.openedSections, section],
        page: 1,
      })
      if (section !== 'skills') void refreshCurrentSection(generation)
    },
    closeSection: (section) => {
      const index = snapshot.openedSections.indexOf(section)
      if (index < 0) return
      const openedSections = snapshot.openedSections.filter((item) => item !== section)
      if (snapshot.section !== section) {
        patch({ openedSections })
        return
      }
      const nextSection = openedSections[Math.min(index, openedSections.length - 1)] ?? 'home'
      const generation = ++requestGeneration
      patch({ openedSections, section: nextSection, page: 1 })
      if (nextSection !== 'skills') void refreshCurrentSection(generation)
    },
    setSearchInput: (value) => {
      const next = typeof value === 'string' ? value.slice(0, 100) : ''
      requestGeneration += 1
      if (searchHandle !== undefined) scheduler.clearTimeout(searchHandle)
      patch({ searchInput: next })
      const generation = requestGeneration
      searchHandle = scheduler.setTimeout(() => {
        searchHandle = undefined
        if (disposed || generation !== requestGeneration) return
        patch({ query: next.trim(), page: 1 })
        void loadProjects(generation)
      }, SEARCH_DEBOUNCE_MS)
    },
    setFilters: async (filters) => {
      const next: MediaProjectHomeFilters = {
        status: filters.status ?? snapshot.filters.status,
        owner: filters.owner ?? snapshot.filters.owner,
        ...(Object.hasOwn(filters, 'scope')
          ? filters.scope === undefined ? {} : { scope: filters.scope }
          : snapshot.filters.scope ? { scope: snapshot.filters.scope } : {}),
      }
      if (!['active', 'archived'].includes(next.status)
        || !['mine', 'all'].includes(next.owner)
        || (next.scope !== undefined && !MEDIA_PROJECT_SCOPES.includes(next.scope))) {
        throw new MediaUserApiError({ code: 'E7009', message: '请求参数有误，请检查后重试' })
      }
      const generation = ++requestGeneration
      patch({ filters: next, page: 1 })
      if (snapshot.section !== 'recycle') await loadProjects(generation)
    },
    setPage: async (page) => {
      if (!Number.isInteger(page) || page < 1) throw new MediaUserApiError({ code: 'E7009', message: '请求参数有误，请检查后重试' })
      const generation = ++requestGeneration
      patch({ page })
      await refreshCurrentSection(generation)
    },
    openProject: (requestedProjectUuid) => {
      policy.require('media.project.view')
      navigation.navigate(`/media-flow/projects/${projectUuid(requestedProjectUuid)}`)
    },
    createProject: async (input) => {
      policy.require('media.project.create')
      const name = projectName(input.name)
      if (!MEDIA_PROJECT_SCOPES.includes(input.scope)) throw new MediaUserApiError({ code: 'E7009', message: '请求参数有误，请检查后重试' })
      await mutate('create', () => api.createMediaProject({ name, scope: input.scope }), (response) => {
        telemetry?.capture('media_project_created', { scope: input.scope, template_used: false })
        navigation.navigate(`/media-flow/projects/${response.data.project.uuid}`)
      })
    },
    renameProject: async (projectUuid, name) => {
      policy.require('media.project.edit')
      const current = snapshot.items.find((item) => item.uuid === projectUuid)
      if (!current) throw new MediaUserApiError({ code: 'E7007', message: '项目不存在或无权访问' })
      await mutate('rename', () => api.updateMediaProject(projectUuid, {
        name: projectName(name),
        description: current.description,
      }), async () => loadProjects())
    },
    updateProjectCover: async (requestedProjectUuid, file) => {
      policy.require('media.asset.upload')
      policy.require('media.project.edit')
      const id = projectUuid(requestedProjectUuid)
      const current = snapshot.items.find((item) => item.uuid === id)
      if (!current) throw new MediaUserApiError({ code: 'E7007', message: '项目不存在或无权访问' })
      const validated = validateMediaUploadFile(file)
      if (validated.modality !== 'image') {
        throw new MediaUserApiError({ code: 'E7009', message: '封面仅支持 PNG、JPG、SVG、GIF 图片' })
      }
      await mutate('cover', async () => {
        const checksum = await coverUploadPort.sha256(file)
        const ticket = await api.createMediaResourceUpload({
          project_uuid: id,
          file_name: validated.file_name,
          content_type: validated.content_type,
          file_size: validated.file_size,
          checksum_sha256: checksum,
        })
        await coverUploadPort.transfer(ticket.data, file)
        const completed = await api.completeMediaResourceUpload(ticket.data.upload_uuid)
        return completed.data.resource_uuid
      }, async (resourceUuid) => {
        await api.updateMediaProject(id, {
          name: current.name,
          description: current.description,
          cover_resource_uuid: resourceUuid,
        })
        // Drop any cached canvas-derived preview so the fresh list load picks
        // the newly uploaded cover instead of reusing stale decoration.
        projectCovers.delete(id)
        projectCoverMisses.delete(id)
        const { [id]: _stale, ...remainingCovers } = snapshot.covers
        patch({ covers: remainingCovers })
        await loadProjects()
      })
    },
    duplicateProject: async (projectUuid, name) => {
      policy.require('media.project.view')
      policy.require('media.project.create')
      await mutate('duplicate', () => api.duplicateMediaProject(projectUuid, { name: projectName(name) }), (response) => {
        navigation.navigate(`/media-flow/projects/${response.data.project.uuid}`)
      })
    },
    archiveProject: async (projectUuid) => {
      policy.require('media.project.delete')
      await mutate('archive', () => api.archiveMediaProject(projectUuid), async () => loadProjects())
    },
    restoreProject: async (requestedProjectUuid) => {
      policy.require('media.project.delete')
      const id = projectUuid(requestedProjectUuid)
      const fromRecycle = snapshot.recycleItems.some((item) => item.uuid === id)
      if (!fromRecycle && !snapshot.items.some((item) => item.uuid === id)) {
        throw new MediaUserApiError({ code: 'E7007', message: '项目不存在或无权访问' })
      }
      await mutate('restore', () => api.restoreMediaProject(id), async () => {
        if (fromRecycle) {
          patch({ purgeRequestedProjectUuids: snapshot.purgeRequestedProjectUuids.filter((item) => item !== id) })
          await loadRecycleBin()
          return
        }
        await loadProjects()
      })
    },
    deleteProject: async (requestedProjectUuid) => {
      policy.require('media.project.delete')
      const id = projectUuid(requestedProjectUuid)
      const current = snapshot.items.find((item) => item.uuid === id)
      if (!current) throw new MediaUserApiError({ code: 'E7007', message: '项目不存在或无权访问' })
      await mutate('delete', () => api.deleteMediaProject(id), () => {
        patch({
          items: snapshot.items.filter((item) => item.uuid !== id),
          total: Math.max(0, snapshot.total - 1),
          purgeRequestedProjectUuids: snapshot.purgeRequestedProjectUuids.filter((item) => item !== id),
        })
      })
    },
    expandProjectScope: async (requestedProjectUuid) => {
      policy.require('media.project.edit')
      const id = projectUuid(requestedProjectUuid)
      const current = snapshot.items.find((item) => item.uuid === id)
      if (!current) throw new MediaUserApiError({ code: 'E7007', message: '项目不存在或无权访问' })
      const targetScope = adjacentProjectScope(current.scope)
      if (!targetScope) throw new MediaUserApiError({ code: 'E7009', message: '请求参数有误，请检查后重试' })
      await mutate('expand', () => api.expandMediaProjectScope(id, { target_scope: targetScope, confirmed: true }), (response) => {
        patch({
          items: snapshot.items.map((item) => item.uuid === id ? response.data.project : item),
        })
      })
    },
    requestProjectPurge: async (requestedProjectUuid) => {
      policy.require('media.project.delete')
      const id = projectUuid(requestedProjectUuid)
      if (!snapshot.recycleItems.some((item) => item.uuid === id)) {
        throw new MediaUserApiError({ code: 'E7007', message: '项目不存在或无权访问' })
      }
      await mutate('purge', () => api.requestMediaProjectPurge(id), async () => {
        if (!snapshot.purgeRequestedProjectUuids.includes(id)) {
          patch({ purgeRequestedProjectUuids: [...snapshot.purgeRequestedProjectUuids, id] })
        }
        await loadRecycleBin()
      })
    },
    dismissActionError: () => patch({ actionError: undefined }),
    dispose: () => {
      disposed = true
      started = false
      requestGeneration += 1
      if (searchHandle !== undefined) scheduler.clearTimeout(searchHandle)
      searchHandle = undefined
      projectCovers.clear()
      projectCoverMisses.clear()
      projectCoverFetches.clear()
    },
  }
  return controller
}

const CAPABILITY_PRESENTATION: Readonly<Record<MediaModality, { readonly icon: string; readonly tone: string; readonly fallbackTitle: string; readonly fallbackCopy: string }>> = {
  file: { icon: 'Frame', tone: 'blue', fallbackTitle: 'PDF 理解', fallbackCopy: '连接文本节点读取文件内容' },
  text: { icon: 'VideoCamera', tone: 'signal', fallbackTitle: '片段重拍', fallbackCopy: '锚点锁定重拍，专业级可控编辑' },
  image: { icon: 'VideoCameraAdd', tone: 'prism', fallbackTitle: '讲解视频', fallbackCopy: '自动配画面，秒出爆款讲解视频' },
  video: { icon: 'Scissor', tone: 'blue', fallbackTitle: '素材混剪', fallbackCopy: '产品素材一键剪成视觉追踪广告' },
  audio: { icon: 'Frame', tone: 'fire', fallbackTitle: '逐帧拉片', fallbackCopy: '智能提取视频亮点' },
}

const CAPABILITY_ICON_PATHS: Readonly<Record<string, readonly string[]>> = {
  "VideoCamera": [
    "M912 302.3L784 376V224c0-35.3-28.7-64-64-64H128c-35.3 0-64 28.7-64 64v576c0 35.3 28.7 64 64 64h592c35.3 0 64-28.7 64-64V648l128 73.7c21.3 12.3 48-3.1 48-27.6V330c0-24.6-26.7-40-48-27.7zM712 792H136V232h576v560zm176-167l-104-59.8V458.9L888 399v226zM208 360h112c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8H208c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8z"
  ],
  "VideoCameraAdd": [
    "M368 724H252V608c0-4.4-3.6-8-8-8h-48c-4.4 0-8 3.6-8 8v116H72c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8h116v116c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V788h116c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8z",
    "M912 302.3L784 376V224c0-35.3-28.7-64-64-64H128c-35.3 0-64 28.7-64 64v352h72V232h576v560H448v72h272c35.3 0 64-28.7 64-64V648l128 73.7c21.3 12.3 48-3.1 48-27.6V330c0-24.6-26.7-40-48-27.7zM888 625l-104-59.8V458.9L888 399v226z",
    "M320 360c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8H208c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8h112z"
  ],
  "Scissor": [
    "M567.1 512l318.5-319.3c5-5 1.5-13.7-5.6-13.7h-90.5c-2.1 0-4.2.8-5.6 2.3l-273.3 274-90.2-90.5c12.5-22.1 19.7-47.6 19.7-74.8 0-83.9-68.1-152-152-152s-152 68.1-152 152 68.1 152 152 152c27.7 0 53.6-7.4 75.9-20.3l90 90.3-90.1 90.3A151.04 151.04 0 00288 582c-83.9 0-152 68.1-152 152s68.1 152 152 152 152-68.1 152-152c0-27.2-7.2-52.7-19.7-74.8l90.2-90.5 273.3 274c1.5 1.5 3.5 2.3 5.6 2.3H880c7.1 0 10.7-8.6 5.6-13.7L567.1 512zM288 370c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80zm0 444c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z"
  ],
  "Frame": [
    "M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z",
    "M719.4 499.1l-296.1-215A15.9 15.9 0 00398 297v430c0 13.1 14.8 20.5 25.3 12.9l296.1-215a15.9 15.9 0 000-25.8zm-257.6 134V390.9L628.5 512 461.8 633.1z"
  ],
  "ArrowRight": [
    "M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z"
  ]
}

function CapabilityIcon({ name }: { readonly name: string }) {
  return <svg width="16" height="16" viewBox="64 64 896 896" fill="#d7d9db" aria-hidden="true" focusable="false" data-mf-capability-icon={name}>
    {CAPABILITY_ICON_PATHS[name].map((path) => <path key={path} d={path} />)}
  </svg>
}

function capabilityCards(models: readonly MediaModelCatalogItem[]) {
  return MEDIA_MODALITIES.filter((modality) => modality !== 'file').map((modality) => ({
    modality,
    modelKey: models.find((candidate) => candidate.modality === modality)?.model_key,
    title: CAPABILITY_PRESENTATION[modality].fallbackTitle,
    copy: CAPABILITY_PRESENTATION[modality].fallbackCopy,
    ...CAPABILITY_PRESENTATION[modality],
  }))
}

function scopeLabel(scope: MediaProjectScope): string {
  if (scope === 'team') return '团队项目'
  if (scope === 'group') return '小组项目'
  return '我的项目'
}

function dateLabel(value: string, fallback = '最近更新'): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return fallback
  return new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit' }).format(date)
}

function remainingTimeLabel(remainingSeconds: number): string {
  const days = Math.ceil(remainingSeconds / 86_400)
  return days > 1 ? `约 ${days} 天可恢复` : '不足 1 天可恢复'
}

function settleMediaHomeEvent(operation: () => Promise<unknown>): void {
  void operation().catch(() => undefined)
}

// Video covers preview the first frame: the #t media fragment plus a seek on
// metadata make browsers paint frame 0 instead of an empty box.
function coverFor(cover: MediaProjectCover | undefined): ReactNode {
  if (cover?.kind === 'image') return <img src={cover.url} alt="" aria-hidden="true" />
  if (cover?.kind === 'video') return <video
    src={`${cover.url}#t=0.001`}
    muted
    playsInline
    preload="metadata"
    aria-hidden="true"
    tabIndex={-1}
    onLoadedMetadata={(event) => { event.currentTarget.currentTime = 0.001 }}
  />
  return <span className="mf-project-card__cover-empty" aria-hidden="true"><ProjectCoverFallbackIcon /></span>
}

function ProjectCardMenu({
  project,
  busy,
  canCreate,
  canEdit,
  canDelete,
  onOpen,
  onRename,
  onEditCover,
  onDuplicate,
  onExpand,
  onArchive,
  onRestore,
  onDelete,
}: {
  readonly project: MediaProjectSummary
  readonly busy: boolean
  readonly canCreate: boolean
  readonly canEdit: boolean
  readonly canDelete: boolean
  readonly onOpen: () => void
  readonly onRename: () => void
  readonly onEditCover: () => void
  readonly onDuplicate: () => void
  readonly onExpand: (targetScope: MediaProjectScope) => void
  readonly onArchive: () => void
  readonly onRestore: () => void
  readonly onDelete: () => void
}) {
  // 扩大到小组尚未开放，暂不展示对应入口。
  const targetScope = project.scope === 'owner' ? null : adjacentProjectScope(project.scope)
  const showLifecycleActions = canDelete
  return <div
    className="mf-project-card__menu"
    role="menu"
    aria-label={`${project.name} 项目操作`}
    onPointerDown={(event) => event.stopPropagation()}
    onKeyDown={(event) => event.stopPropagation()}
  >
    <button type="button" role="menuitem" className="mf-project-card__menu-item" disabled={busy} onClick={onOpen}>打开</button>
    {canEdit ? <button type="button" role="menuitem" className="mf-project-card__menu-item" disabled={busy} onClick={onRename}>重命名</button> : null}
    {canEdit ? <button type="button" role="menuitem" className="mf-project-card__menu-item" disabled={busy} onClick={onEditCover}>编辑封面</button> : null}
    {canCreate ? <button type="button" role="menuitem" className="mf-project-card__menu-item" disabled={busy} onClick={onDuplicate}>创建副本</button> : null}
    {canEdit && targetScope ? <div className="mf-project-card__menu-separator" role="separator" /> : null}
    {canEdit && targetScope ? <button type="button" role="menuitem" className="mf-project-card__menu-item" disabled={busy} onClick={() => onExpand(targetScope)}>{project.scope === 'owner' ? '扩大到小组' : '扩大到团队'}</button> : null}
    {showLifecycleActions ? project.status === 'archived'
      ? <button type="button" role="menuitem" className="mf-project-card__menu-item" disabled={busy} onClick={onRestore}>恢复</button>
      : <button type="button" role="menuitem" className="mf-project-card__menu-item" disabled={busy} onClick={onArchive}>归档</button> : null}
    {showLifecycleActions ? <div className="mf-project-card__menu-separator" role="separator" /> : null}
    {showLifecycleActions ? <button type="button" role="menuitem" className="mf-project-card__menu-item" disabled={busy} onClick={onDelete}>删除项目</button> : null}
  </div>
}

function ProjectCoverFallbackIcon() {
  return <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" className="mf-project-card__cover-icon">
    <path d="M1003.153333 404.96a52.933333 52.933333 0 0 0-42.38-20.96H896V266.666667a53.393333 53.393333 0 0 0-53.333333-53.333334H461.253333a10.573333 10.573333 0 0 1-7.54-3.126666L344.46 100.953333A52.986667 52.986667 0 0 0 306.746667 85.333333H53.333333a53.393333 53.393333 0 0 0-53.333333 53.333334v704a53.393333 53.393333 0 0 0 53.333333 53.333333h796.893334a53.453333 53.453333 0 0 0 51.453333-39.333333l110.546667-405.333334a52.953333 52.953333 0 0 0-9.073334-46.373333zM53.333333 128h253.413334a10.573333 10.573333 0 0 1 7.54 3.126667l109.253333 109.253333A52.986667 52.986667 0 0 0 461.253333 256H842.666667a10.666667 10.666667 0 0 1 10.666666 10.666667v117.333333H173.773333a53.453333 53.453333 0 0 0-51.453333 39.333333L42.666667 715.366667V138.666667a10.666667 10.666667 0 0 1 10.666666-10.666667z m917.726667 312.14l-110.546667 405.333333a10.666667 10.666667 0 0 1-10.286666 7.86H63.226667a10.666667 10.666667 0 0 1-10.286667-13.473333l110.546667-405.333333A10.666667 10.666667 0 0 1 173.773333 426.666667h787a10.666667 10.666667 0 0 1 10.286667 13.473333z" fill="#424242" />
  </svg>
}

function HomeNavigationIcon() {
  return <svg width="20" height="20" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M958.976 504.7296L561.5104 107.52l-26.624-26.624a32.4096 32.4096 0 0 0-45.6704 0L65.1264 504.6784a65.7408 65.7408 0 0 0-19.3024 47.3088 66.4576 66.4576 0 0 0 66.7136 65.1264h43.7248v335.0016H867.84v-335.0016h44.6464a65.536 65.536 0 0 0 60.8768-40.704 65.4848 65.4848 0 0 0-14.3872-71.68z m-389.3248 373.4016h-115.2v-209.8688h115.2v209.8688z m224.1536-335.0016v335.0016h-158.3104v-234.496a41.1136 41.1136 0 0 0-41.1648-41.1648H429.7728a41.1136 41.1136 0 0 0-41.1648 41.1136v234.496H230.4v-334.9504H131.584l380.5696-380.3136 23.7568 23.808 356.7104 356.4544h-98.816z" fill="currentColor" />
  </svg>
}

function ProjectsNavigationIcon() {
  return <svg width="20" height="20" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M905.1136 356.5056h-237.7216V118.784a18.3296 18.3296 0 0 0-18.2784-18.2784H118.8352a18.3296 18.3296 0 0 0-18.2784 18.2784v530.2784c0 10.0864 8.192 18.2784 18.2784 18.2784h237.7216v237.7216c0 10.0864 8.192 18.2784 18.2784 18.2784h530.2784c10.0864 0 18.2784-8.192 18.2784-18.2784V374.784a18.3296 18.3296 0 0 0-18.2784-18.2784zM356.5568 374.784v214.8864H178.2784V178.176h411.4432v178.2784H374.784a18.3296 18.3296 0 0 0-18.2784 18.2784z m233.1648 59.392v155.4944H434.2784V434.176h155.4432z m256 411.4944H434.2784v-178.3296h214.8352c10.0864 0 18.2784-8.192 18.2784-18.2784V434.2272h178.3296v411.4432z" fill="currentColor" />
  </svg>
}

function RecycleNavigationIcon() {
  return <svg width="20" height="20" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M154.6 854.28c-13.03 0-25.07-6.93-31.62-18.2-6.56-11.3-6.6-25.25-0.09-36.57L279.5 526.63c10.05-17.52 32.41-23.59 49.92-13.52 17.52 10.05 23.57 32.39 13.51 49.91L217.86 780.95l251.26-0.73h0.11c20.15 0 36.51 16.3 36.57 36.46 0.06 20.2-16.27 36.62-36.46 36.68l-314.62 0.91c-0.05 0.01-0.09 0.01-0.12 0.01z" fill="currentColor" />
    <path d="M335.74 672.47c-16.34 0-31.23-11.02-35.43-27.59l-21.91-86.52c-4.96-19.59 6.89-39.48 26.47-44.43 19.59-5 39.48 6.88 44.44 26.46l21.91 86.52c4.96 19.59-6.89 39.48-26.47 44.43-3.02 0.77-6.04 1.13-9.01 1.13z" fill="currentColor" />
    <path d="M218.95 605.63c-16.35 0-31.23-11.04-35.43-27.61-4.96-19.57 6.9-39.46 26.48-44.41l86.51-21.89c19.59-4.96 39.47 6.89 44.43 26.48 4.96 19.57-6.9 39.46-26.48 44.41l-86.51 21.89c-3.01 0.78-6.03 1.13-9 1.13zM562.26 847.2c-19.98 0-36.3-16.07-36.56-36.11-0.25-20.2 15.91-36.77 36.11-37.04l251.24-3.2L684.6 554.94c-10.33-17.36-4.62-39.8 12.73-50.12s39.8-4.62 50.12 12.73L908.3 787.94a36.513 36.513 0 0 1 0.47 36.57 36.533 36.533 0 0 1-31.44 18.7l-314.6 4c-0.16-0.01-0.31-0.01-0.47-0.01z" fill="currentColor" />
    <path d="M564.84 842.61c-9.4 0-18.8-3.61-25.96-10.8-14.23-14.34-14.14-37.48 0.19-51.71l63.33-62.88c14.33-14.23 37.48-14.16 51.72 0.18 14.23 14.34 14.14 37.48-0.19 51.71l-63.33 62.88c-7.13 7.09-16.45 10.62-25.76 10.62z" fill="currentColor" />
    <path d="M630.06 914.29c-9.4 0-18.8-3.61-25.96-10.8l-62.88-63.34c-14.23-14.34-14.14-37.5 0.19-51.71 14.34-14.23 37.49-14.16 51.72 0.18l62.88 63.34c14.23 14.34 14.14 37.5-0.19 51.71-7.12 7.09-16.44 10.62-25.76 10.62zM665.63 487.72c-12.64 0-24.94-6.55-31.71-18.29L508.33 251.81l-125.6 217.62c-10.1 17.5-32.46 23.46-49.96 13.39-17.5-10.11-23.49-32.46-13.4-49.96l157.28-272.5a36.594 36.594 0 0 1 31.68-18.29c13.07 0 25.14 6.96 31.68 18.29l157.27 272.5c10.09 17.5 4.1 39.86-13.4 49.96a36.328 36.328 0 0 1-18.25 4.9z" fill="currentColor" />
    <path d="M660.35 487.74c-3.33 0-6.71-0.46-10.06-1.43l-85.8-24.52c-19.42-5.55-30.67-25.79-25.12-45.21 5.55-19.41 25.81-30.7 45.21-25.11l85.8 24.52c19.42 5.55 30.67 25.79 25.12 45.21-4.6 16.06-19.24 26.54-35.15 26.54z" fill="currentColor" />
    <path d="M666.47 481.61c-3.33 0-6.71-0.46-10.06-1.43-19.42-5.55-30.67-25.79-25.12-45.21l24.52-85.8c5.55-19.41 25.79-30.66 45.21-25.11 19.42 5.55 30.67 25.79 25.12 45.21l-24.52 85.8c-4.59 16.06-19.24 26.54-35.15 26.54z" fill="currentColor" />
  </svg>
}

function SidebarCollapseIcon() {
  return <svg className="mf-home__collapse-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M4 12L8 8L4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="15.25" y="15.25" width="14.5" height="14.5" rx="3.25" transform="rotate(-180 15.25 15.25)" stroke="currentColor" strokeWidth="1.5" />
    <line x1="11.25" y1="15" x2="11.25" y2="1" stroke="currentColor" strokeWidth="1.5" />
  </svg>
}

function PlusIcon() {
  return <svg className="mf-home__primary-icon" width="16" height="16" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M469.333333 469.333333V170.666667h85.333334v298.666666h298.666666v85.333334h-298.666666v298.666666h-85.333334v-298.666666H170.666667v-85.333334h298.666666z" fill="currentColor" />
  </svg>
}

function HomeStyles() {
  return <style data-mf-home-style dangerouslySetInnerHTML={{ __html: `
    .mf-home{--mf-home-sidebar-width:228px;height:calc(100vh - 70px);height:calc(100dvh - 70px);overflow:hidden;display:grid;grid-template-columns:var(--mf-home-sidebar-width) minmax(0,1fr);color:#f5f4f7;background:#050607;font-family:Inter,"PingFang SC","Microsoft YaHei",sans-serif}.mf-home *{box-sizing:border-box}.mf-home[data-mf-sidebar-collapsed="true"]{--mf-home-sidebar-width:72px}
    .mf-home__sidebar{position:fixed;top:70px;bottom:0;left:0;z-index:4;width:var(--mf-home-sidebar-width);overflow-y:auto;display:flex;flex-direction:column;padding:16px;border-right:1px solid rgba(255,255,255,.08);background:#1C1C1C}.mf-home__nav{display:grid;gap:8px}.mf-home__nav-button,.mf-home__collapse{font-weight:400;transform:none;transition:color 120ms ease,background-color 120ms ease}.mf-home__sidebar .mf-home__nav-button,.mf-home__sidebar .mf-home__collapse{translate:none;scale:none}.mf-home__nav-button{display:flex;align-items:center;justify-content:flex-start;gap:13px;width:100%;height:44px;border:0;border-radius:9px;padding:0 12px;color:#aaa8ae;background:transparent;cursor:pointer;font-size:14px;text-align:left}.mf-home .mf-home__nav-button.media-ui-button[aria-current="page"]{color:#fff!important;background:#202325!important;border-color:transparent!important}.mf-home__nav-icon{display:grid;place-items:center;flex:0 0 20px;width:20px}.mf-home__collapse-area{margin-top:auto;border-top:1px solid rgba(255,255,255,.12);padding-top:8px}.mf-home__collapse{display:flex;align-items:center;justify-content:flex-start;gap:13px;width:100%;height:44px;border:0;padding:0 12px;color:#8d8b91;background:transparent;cursor:pointer;font-size:13px;text-align:left}.mf-home__sidebar .media-ui-button:not(:disabled):is(:hover,:active){animation:none!important;filter:none!important;transform:none!important;translate:none!important;scale:none!important;rotate:none!important;opacity:1!important;box-shadow:none!important;text-shadow:none!important;font-weight:400!important;font-variation-settings:normal!important;letter-spacing:normal!important;border:0 solid transparent!important;margin:0!important;transition:color 120ms ease,background-color 120ms ease!important}.mf-home__sidebar .mf-home__nav-button:not(:disabled):is(:hover,:active),.mf-home__sidebar .mf-home__collapse:not(:disabled):is(:hover,:active){position:relative!important;inset:auto!important;width:100%!important;min-width:0!important;max-width:none!important;height:44px!important;min-height:44px!important;max-height:44px!important;padding:0 12px!important;gap:13px!important}.mf-home__sidebar .media-ui-button:not(:disabled):is(:hover,:active) :is(span,svg,path,rect,line),.mf-home__sidebar .media-ui-button:not(:disabled):is(:hover,:active)::before,.mf-home__sidebar .media-ui-button:not(:disabled):is(:hover,:active)::after{animation:none!important;transition:none!important;filter:none!important;translate:none!important;scale:none!important;rotate:none!important;opacity:1!important;box-shadow:none!important;text-shadow:none!important;font-weight:inherit!important;font-variation-settings:inherit!important;letter-spacing:inherit!important}.mf-home__sidebar .media-ui-button:not(:disabled):is(:hover,:active) :is(span,svg,path,rect,line){margin:0!important;padding:0!important}.mf-home__sidebar .media-ui-button:not(:disabled):is(:hover,:active) :is(span,svg){transform:none!important}.mf-home__sidebar .mf-home__nav-button.media-ui-button:not(:disabled):is(:hover,:active),.mf-home__sidebar .mf-home__collapse.media-ui-button:not(:disabled):is(:hover,:active){color:#fff!important}.mf-home__sidebar .mf-home__nav-button.media-ui-button:not([aria-current="page"]):not(:disabled):is(:hover,:active),.mf-home__sidebar .mf-home__collapse.media-ui-button:not(:disabled):is(:hover,:active){background:transparent!important}.mf-home__collapse-icon{transform:rotate(180deg)}.mf-home__sidebar .mf-home__collapse:not(:disabled):is(:hover,:active) .mf-home__collapse-icon{transform:rotate(180deg)!important}.mf-home[data-mf-sidebar-collapsed="true"] .mf-home__sidebar{padding:16px 8px}.mf-home[data-mf-sidebar-collapsed="true"] .mf-home__nav-button,.mf-home[data-mf-sidebar-collapsed="true"] .mf-home__collapse{flex-direction:column;justify-content:center;height:56px;padding:6px 0;gap:4px;font-size:11px;line-height:16px;text-align:center}.mf-home[data-mf-sidebar-collapsed="true"] .mf-home__sidebar .media-ui-button:not(:disabled):is(:hover,:active){height:56px!important;min-height:56px!important;max-height:56px!important;padding:6px 0!important;gap:4px!important}.mf-home[data-mf-sidebar-collapsed="true"] .mf-home__collapse-icon{transform:none}.mf-home[data-mf-sidebar-collapsed="true"] .mf-home__sidebar .mf-home__collapse:not(:disabled):is(:hover,:active) .mf-home__collapse-icon{transform:none!important}.mf-home[data-mf-sidebar-collapsed="true"] .mf-home__nav-label,.mf-home[data-mf-sidebar-collapsed="true"] .mf-home__collapse-label{display:block;white-space:nowrap}
    .mf-home__stage{grid-column:2;height:100%;min-height:0;display:flex;flex-direction:column;overflow:hidden;min-width:0}.mf-home__stage>[data-mf-tab-placeholder]{flex:0 0 auto}.mf-home__tabs{position:fixed;top:70px;left:var(--mf-home-sidebar-width);right:0;z-index:3;min-height:48px;display:flex;flex-wrap:wrap;align-items:center;gap:4px;padding:0 8px 10px;border:1px solid #353537;background:#171717}.mf-home__tab-item{position:relative;height:30px;margin-top:8px;min-width:0;max-width:calc(50% - 2px);flex-shrink:0;overflow:hidden;border:1px solid rgba(255,255,255,.1);border-radius:8px;color:rgba(255,255,255,.6);background:rgba(0,0,0,.3);font-size:12px;transition:none}.mf-home__tab-item:hover{border-color:rgba(255,255,255,.2);color:#fff}.mf-home__tab-item[data-selected="true"]{color:#00FAE5;border-color:rgba(255,255,255,.12);background:rgba(24,24,24,.8);-webkit-backdrop-filter:blur(16px);backdrop-filter:blur(16px);box-shadow:inset 0 1px 0 rgba(255,255,255,.05)}
    .mf-home__tab-mirror{position:absolute;inset:0;z-index:3;pointer-events:none;border-radius:inherit;background:linear-gradient(110deg,transparent 30%,rgba(255,255,255,.08) 40%,rgba(255,255,255,.4) 49%,rgba(255,255,255,.12) 56%,transparent 66%);animation:mf-home-tab-mirror 650ms ease-out both}
    @keyframes mf-home-tab-mirror{0%{transform:translateX(-110%);opacity:0}15%,75%{opacity:1}100%{transform:translateX(110%);opacity:0}}
    @media(prefers-reduced-motion:reduce){.mf-home__tab-mirror{display:none;animation:none}}
    .mf-home__tab,.mf-home__tab-close{color:inherit;background:transparent;border:0;font:inherit;cursor:pointer;transition:none}.mf-home__tab{display:flex;align-items:center;justify-content:flex-start;gap:0;width:100%;height:100%;min-width:0;padding:0 28px 0 8px;border-radius:8px;text-align:left}.mf-home__tab-label{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.mf-home__tab-close{position:absolute;right:8px;top:50%;z-index:2;transform:translateY(-50%);display:flex;align-items:center;justify-content:center;width:17px;height:17px;padding:0;border-radius:4px;line-height:1;min-width:0}.mf-home__tab-close:hover{color:#fff;background:rgba(255,255,255,.1)}.mf-home__tab-item:has(:focus-visible){outline:2px solid #00FAE5;outline-offset:2px}@media(min-width:640px){.mf-home__tab-item{max-width:calc(33.333% - 3px)}}@media(min-width:1024px){.mf-home__tab-item{max-width:148px}}
    .mf-home__content{flex:1 1 auto;min-height:0;overflow-y:auto;overscroll-behavior:contain;padding:24px;min-width:0}.mf-home__hero{--mf-home-hero-height:clamp(480px,calc(480px + (100vw - 1440px) * 5 / 24),580px);display:grid;grid-template-columns:minmax(0,1fr) minmax(0,min(66%,calc((var(--mf-home-hero-height) - 2px - 40px - 18px) * 16 / 9 + 38px)));grid-template-rows:minmax(0,1fr);gap:18px;box-sizing:border-box;height:var(--mf-home-hero-height);padding:0;border:1px solid rgba(255,255,255,.05);border-radius:18px;background:#0b0c0d}.mf-home__hero-copy{min-width:0;min-height:0;align-self:stretch;position:relative;overflow:hidden;display:flex;flex-direction:column;justify-content:center;align-items:center;padding:32px clamp(18px,3vw,46px);border-radius:14px;background:radial-gradient(circle at 48% 46%,rgba(119,107,255,.12),transparent 58%)}.mf-home__plasma{position:absolute;inset:0;overflow:hidden;pointer-events:none}.mf-home__hero-foreground{flex:1;min-width:0;min-height:0;width:fit-content;max-width:100%;display:flex;flex-direction:column;justify-content:center;align-items:flex-start;position:relative;z-index:1}.mf-home__hero h1{margin:0;max-width:330px;font-size:clamp(16px,1.7vw,24px);line-height:1.75;letter-spacing:.01em}.mf-home__primary{margin-top:22px;min-width:min(142px,100%);max-width:100%;height:42px;border:0;border-radius:8px;color:#151519;background:#f6f5f8;cursor:pointer;font-weight:600}.mf-home__primary:disabled{opacity:.5;cursor:wait}.mf-home__capabilities{min-width:0;align-self:center;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));align-items:start;gap:18px;margin:20px 20px 20px 0}.mf-home__capability{box-sizing:border-box;min-width:0;min-height:0;aspect-ratio:16/9;position:relative;overflow:hidden;display:flex;flex-direction:column;justify-content:flex-end;padding:clamp(8px,1.3vw,18px);border-radius:10px;background:#15171b}.mf-home__capability:before{content:"";position:absolute;inset:0;opacity:.84;background:var(--mf-capability-background) center/cover no-repeat}.mf-home__capability[data-tone="signal"]{--mf-capability-background:url('${reshootBackground.src}')}.mf-home__capability[data-tone="prism"]{--mf-capability-background:url('${explainerBackground.src}')}.mf-home__capability[data-tone="blue"]{--mf-capability-background:url('${montageBackground.src}')}.mf-home__capability[data-tone="fire"]{--mf-capability-background:url('${frameAnalysisBackground.src}')}.mf-home__capability-status{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);color:#fff;font-size:clamp(12px,1.2vw,18px);font-weight:700;line-height:1.5;white-space:nowrap;text-shadow:0 2px 8px rgba(0,0,0,.65);pointer-events:none}.mf-home__capability-panel{position:absolute;left:4px;right:4px;bottom:4px;height:64px;max-height:calc(100% - 8px);border-radius:8px;pointer-events:none;background:rgba(0,0,0,.6);opacity:0}.mf-home__capability-copy{min-width:0;position:absolute;left:19px;right:19px;bottom:14px}.mf-home__capability strong{display:flex;align-items:center;gap:6px;overflow:hidden;white-space:nowrap;font-size:14px;font-weight:500;line-height:20px;color:#f6f6f6}.mf-home__capability strong svg{flex-shrink:0}.mf-home__capability-description{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;margin-top:4px;font-size:14px;font-weight:400;line-height:20px;color:#c8c8c8}.mf-home__capability-arrow{position:absolute;right:19px;bottom:20px;width:32px;height:32px;display:grid;place-items:center;border-radius:50%;background:#2c2c2c;color:#8c8c8c;opacity:0;pointer-events:none}.mf-home__capability:is(:hover,:focus-visible) .mf-home__capability-panel,.mf-home__capability:is(:hover,:focus-visible) .mf-home__capability-arrow{opacity:1}.mf-home__capability:is(:hover,:focus-visible) .mf-home__capability-copy{right:61px}.mf-home__capability:focus-visible{outline:2px solid #c8c8c8;outline-offset:2px}@media(hover:none){.mf-home__capability-panel{opacity:1}.mf-home__capability-arrow{display:none}.mf-home__capability:is(:hover,:focus-visible) .mf-home__capability-copy{right:19px}}
    .mf-home__projects{margin:0}.mf-home__projects-head{display:flex;align-items:flex-end;justify-content:space-between;gap:18px;margin-bottom:16px}.mf-home__projects h2{margin:0 0 14px;font-size:18px;font-weight:600}.mf-home__projects[data-mf-recycle="true"] h2{margin-bottom:0}.mf-home__filters{display:flex;flex-wrap:wrap;gap:8px}.mf-home__search{min-width:280px;max-width:300px}.mf-home__grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:18px}.mf-project-card{min-width:0;overflow:hidden;border:1px solid transparent;border-radius:10px;background:transparent}.mf-project-card__cover{position:relative;aspect-ratio:16/9;display:grid;place-items:center;overflow:hidden;border-radius:9px;background:#1A1A1A}button.mf-project-card__cover{width:100%;border:0;padding:0;font:inherit;cursor:pointer}button.mf-project-card__cover:focus-visible{outline:2px solid #52e9df;outline-offset:-2px}button.mf-project-card__cover:disabled{cursor:wait}.mf-project-card__cover :is(img,video){width:100%;height:100%;object-fit:cover}.mf-project-card__cover video{display:block;pointer-events:none}.mf-project-card__cover span{display:grid;place-items:center;width:100%;height:100%}.mf-project-card__cover-icon{width:35px;height:35px}.mf-project-card__body{position:relative;padding:12px}.mf-project-card__title-row{display:flex;align-items:center;gap:8px;min-height:24px}.mf-project-card__title-row strong{display:block;min-width:0;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:14px}.mf-project-card__more{display:grid;place-items:center;flex:0 0 24px;width:24px;height:24px;border:0;border-radius:8px;padding:0;color:#f6f6f6;background:transparent;cursor:pointer;font:inherit;font-size:18px;line-height:24px}.mf-project-card__more:not(:disabled):is(:hover,:focus-visible),.mf-project-card__more[aria-expanded="true"]{background:#272727;outline:none}.mf-project-card__more:focus-visible{box-shadow:inset 0 0 0 1px #f6f6f6}.mf-project-card__more:disabled{opacity:.45;cursor:wait}.mf-project-card__meta{display:flex;justify-content:space-between;gap:8px;margin-top:6px;color:#aaa6af;font-size:11px}.mf-project-card__actions{display:flex;flex-wrap:wrap;gap:6px;min-height:28px;margin-top:11px;transition:opacity 140ms ease-out,transform 140ms ease-out}.mf-project-card__actions button{height:28px;border:1px solid rgba(255,255,255,.1);border-radius:6px;padding:0 8px;color:#d1cdd5;background:#202123;cursor:pointer;font-size:11px}.mf-project-card__actions button:first-child{color:#fff;background:#343038}.mf-project-card__actions button:disabled{opacity:.45;cursor:wait}.mf-project-card__menu{display:grid;width:154px;padding:4px;border-radius:4px;background:#292929;box-shadow:0 4px 4px rgba(0,0,0,.25)}.mf-project-card__menu-item{display:flex;align-items:center;width:100%;height:36px;border:0;border-radius:2px;padding:0 9px;color:#f6f6f6;background:transparent;cursor:pointer;font:inherit;font-size:14px;line-height:20px;text-align:left}.mf-project-card__menu-item:not(:disabled):is(:hover,:focus-visible){background:#3f3f3f;outline:none}.mf-project-card__menu-item:focus-visible{box-shadow:inset 0 0 0 1px #f6f6f6}.mf-project-card__menu-item:disabled{color:#f6f6f6;cursor:not-allowed}.mf-project-card__menu-separator{height:1px;margin:4px 0;background:rgba(255,255,255,.12)}.mf-project-menu-overlay .flowx-media-ant-popover-inner{padding:0!important;background:transparent!important;box-shadow:none!important}.mf-project-menu-overlay .flowx-media-ant-popover-content{padding:0!important}.mf-home__skeleton,.mf-home__hero-skeleton-copy,.mf-home__capability-skeleton{background:linear-gradient(100deg,#111 25%,#1b1b1d 45%,#111 65%);background-size:200% 100%;animation:mf-home-shimmer 1.4s linear infinite}.mf-home__skeleton{aspect-ratio:16/10;border-radius:10px}.mf-home__hero-skeleton-copy{min-width:0;min-height:0;border-radius:14px}.mf-home__capability-skeleton:before{display:none}.mf-home__state{min-height:220px;display:grid;place-items:center;border:1px dashed rgba(255,255,255,.13);border-radius:12px;color:#aaa6af;text-align:center}.mf-home__state strong{display:block;color:#eeebf2;margin-bottom:7px}.mf-home__state button{margin-top:12px;height:34px;border:0;border-radius:7px;padding:0 14px;cursor:pointer}.mf-home__error{margin-bottom:14px;padding:12px 14px;border:1px solid rgba(255,103,115,.28);border-radius:9px;color:#ffb5bb;background:rgba(107,28,37,.25);font-size:13px}.mf-home__error code{margin-right:7px}.mf-home__pagination{display:flex;justify-content:flex-end;align-items:center;gap:10px;margin-top:18px;color:#aaa6af;font-size:12px}.mf-home__pagination button{height:30px;border:1px solid rgba(255,255,255,.1);border-radius:6px;padding:0 10px;color:#d4d1d7;background:#191a1c}.mf-home__pagination button:disabled{opacity:.4}.mf-home__dialog-actions{display:flex;justify-content:flex-end;gap:8px;margin-top:20px}
    .mf-home__loading-items{display:contents}.mf-home__grid>.mf-home__state{grid-column:1/-1}.mf-project-card--create{align-self:start;border-color:transparent;background:transparent}.mf-project-card--create .mf-project-card__create{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;border:1px solid #707070;border-radius:9px;color:#d7d7d7;transition:border-color 120ms ease,background-color 120ms ease}.mf-project-card__create .mf-home__primary-icon{width:35px;height:35px;flex-shrink:0}.mf-project-card__create strong{text-align:center;font-size:14px;font-weight:500;line-height:20px}.mf-project-card--create .mf-project-card__create:not(:disabled):hover{border-color:#aaa;background:#242424}.mf-project-card--create .mf-project-card__create:disabled{opacity:.5}.mf-project-card--create .mf-project-card__body{color:inherit}
    @media(width < 1080px){.mf-project-card__create strong{display:none}}
    @keyframes mf-home-shimmer{to{background-position:-200% 0}}.mf-home__cover-input{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap}@media (hover:hover) and (pointer:fine){.mf-project-card__actions{opacity:0;transform:translateY(4px);pointer-events:none}.mf-project-card:hover .mf-project-card__actions,.mf-project-card:focus-within .mf-project-card__actions{opacity:1;transform:none;pointer-events:auto}}@media (hover:none), (pointer:coarse){.mf-project-card__actions{opacity:1;transform:none;pointer-events:auto}}@media(min-width:1200px)and(max-width:1439px){.mf-home__grid{grid-template-columns:repeat(3,minmax(0,1fr))}}@media(min-width:768px)and(max-width:1199px){.mf-home__grid{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:767px){.mf-home{--mf-home-sidebar-width:56px}.mf-home__sidebar,.mf-home[data-mf-sidebar-collapsed="true"] .mf-home__sidebar{padding:14px 8px}.mf-home__nav-button,.mf-home__collapse{justify-content:center;padding-left:0;padding-right:0}.mf-home__sidebar .media-ui-button:not(:disabled):is(:hover,:active){padding-left:0!important;padding-right:0!important}.mf-home__nav-label,.mf-home__collapse-label{display:none}.mf-home__tabs{top:70px}.mf-home__content{padding:14px}.mf-home__hero{grid-template-columns:minmax(0,1fr);grid-template-rows:minmax(0,1fr) auto}.mf-home__hero-copy{padding:18px}.mf-home__capabilities{width:calc(100% - 20px);max-width:calc((var(--mf-home-hero-height) - 2px - 180px - 18px - 24px - 18px) * 16 / 9 + 18px);justify-self:end;margin:12px 20px 12px 0}.mf-home__capability{padding:8px}.mf-home__capability-copy{left:8px;right:8px;bottom:8px}.mf-home__capability strong{gap:4px;font-size:12px;line-height:16px}.mf-home__capability-description{margin-top:2px;font-size:10px;line-height:14px}.mf-home__capability-arrow{display:none}.mf-home__capability:is(:hover,:focus-visible) .mf-home__capability-copy{right:8px}.mf-home__projects-head{align-items:stretch;flex-direction:column}.mf-home__search{width:100%;min-width:0;max-width:none}.mf-home__grid{grid-template-columns:1fr}}@media(prefers-reduced-motion:reduce){.mf-home__skeleton,.mf-home__hero-skeleton-copy,.mf-home__capability-skeleton{animation:none}.mf-project-card__actions{transition:none}}
  ` }} />
}

type DialogState =
  | { readonly kind: 'create'; readonly name: string; readonly scope: MediaProjectScope }
  | { readonly kind: 'rename'; readonly project: MediaProjectSummary; readonly name: string }
  | { readonly kind: 'delete'; readonly project: MediaProjectSummary }
  | { readonly kind: 'expand'; readonly project: MediaProjectSummary; readonly targetScope: MediaProjectScope }
  | { readonly kind: 'purge'; readonly project: MediaRecycleBinProject }
  | undefined

export function MediaProjectHome({ controller, skillsContent }: { readonly controller: MediaProjectHomeController; readonly skillsContent?: ReactNode }) {
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot)
  const [dialog, setDialog] = useState<DialogState>()
  const [openProjectMenuUuid, setOpenProjectMenuUuid] = useState<string>()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [coverPickerProject, setCoverPickerProject] = useState<MediaProjectSummary>()
  const coverInputRef = useRef<HTMLInputElement>(null)
  const tabStripRef = useRef<HTMLDivElement>(null)
  const [tabStripHeight, setTabStripHeight] = useState(50)
  const [tabSweep, setTabSweep] = useState<{ section: MediaProjectHomeSection; sequence: number } | null>(null)
  const tabSweepSequence = useRef(0)
  const selectTab = (section: MediaProjectHomeSection) => {
    setTabSweep({ section, sequence: ++tabSweepSequence.current })
    controller.setSection(section)
  }

  useEffect(() => {
    settleMediaHomeEvent(() => controller.start())
    return () => controller.dispose()
  }, [controller])

  const busy = snapshot.action !== undefined
  const capabilities = capabilityCards(snapshot.models)
  const canCreate = controller.can('media.project.create')
  const canEdit = controller.can('media.project.edit')
  const canDelete = controller.can('media.project.delete')
  const visibleSections = snapshot.openedSections.filter((section) => (section !== 'recycle' || canDelete) && (section !== 'skills' || snapshot.skillsEnabled))

  useEffect(() => {
    const strip = tabStripRef.current
    if (!strip) return
    const measure = () => setTabStripHeight(strip.getBoundingClientRect().height)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(strip)
    return () => observer.disconnect()
  }, [visibleSections.length])

  const showingRecycle = snapshot.section === 'recycle'
  const visibleProjects = showingRecycle ? snapshot.recycleItems : snapshot.items
  const openCreate = () => setDialog({ kind: 'create', name: '未命名工作区', scope: 'owner' })
  // The single hidden picker is re-armed per menu click: selecting a project
  // first and then clicking the native dialog keeps one input for every card.
  const requestCoverUpload = (project: MediaProjectSummary) => {
    setCoverPickerProject(project)
    coverInputRef.current?.click()
  }
  const onCoverFilePicked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    const target = coverPickerProject
    setCoverPickerProject(undefined)
    if (!file || !target) return
    settleMediaHomeEvent(() => controller.updateProjectCover(target.uuid, file))
  }
  const submitDialog = async () => {
    if (!dialog) return
    if (dialog.kind === 'create') await controller.createProject({ name: dialog.name, scope: dialog.scope })
    if (dialog.kind === 'rename') await controller.renameProject(dialog.project.uuid, dialog.name)
    if (dialog.kind === 'delete') await controller.deleteProject(dialog.project.uuid)
    if (dialog.kind === 'expand') await controller.expandProjectScope(dialog.project.uuid)
    if (dialog.kind === 'purge') await controller.requestProjectPurge(dialog.project.uuid)
    setDialog(undefined)
  }

  return <main className="mf-home" data-mf-media-home={true} data-mf-sidebar-collapsed={sidebarCollapsed}>
    <HomeStyles />
    <input
      ref={coverInputRef}
      type="file"
      className="mf-home__cover-input"
      accept={PROJECT_COVER_ACCEPT}
      aria-hidden="true"
      tabIndex={-1}
      onChange={onCoverFilePicked}
    />
    <aside className="mf-home__sidebar" aria-label="AI媒体流导航">
      <nav className="mf-home__nav">
        <MediaButton type="button" variant="text" className="mf-home__nav-button" aria-label="首页" aria-current={snapshot.section === 'home' ? 'page' : undefined} onClick={() => controller.setSection('home')}><span className="mf-home__nav-icon"><HomeNavigationIcon /></span><span className="mf-home__nav-label">首页</span></MediaButton>
        <MediaButton type="button" variant="text" className="mf-home__nav-button" aria-label="项目" aria-current={snapshot.section === 'projects' ? 'page' : undefined} onClick={() => controller.setSection('projects')}><span className="mf-home__nav-icon"><ProjectsNavigationIcon /></span><span className="mf-home__nav-label">项目</span></MediaButton>
        {canDelete ? <MediaButton type="button" variant="text" className="mf-home__nav-button" aria-label="回收站" aria-current={showingRecycle ? 'page' : undefined} onClick={() => controller.setSection('recycle')}><span className="mf-home__nav-icon"><RecycleNavigationIcon /></span><span className="mf-home__nav-label">回收站</span></MediaButton> : null}
        {snapshot.skillsEnabled ? <MediaButton type="button" variant="text" className="mf-home__nav-button" aria-label="Skills" aria-current={snapshot.section === 'skills' ? 'page' : undefined} onClick={() => controller.setSection('skills')}><span className="mf-home__nav-icon" aria-hidden="true">⌘</span><span className="mf-home__nav-label">Skills</span></MediaButton> : null}
      </nav>
      <div className="mf-home__collapse-area">
        <MediaButton type="button" variant="text" className="mf-home__collapse" aria-label={sidebarCollapsed ? '展开侧栏' : '收起侧栏'} aria-expanded={!sidebarCollapsed} onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}><span className="mf-home__nav-icon"><SidebarCollapseIcon /></span><span className="mf-home__collapse-label">{sidebarCollapsed ? '展开' : '收起'}</span></MediaButton>
      </div>
    </aside>

    <section className="mf-home__stage">
      {visibleSections.length > 0 ? <div data-mf-tab-placeholder={true} style={{ height: tabStripHeight }}>
      <div ref={tabStripRef} className="mf-home__tabs" role="tablist" aria-label="媒体流页面">
        {visibleSections.map((section) => {
          const label = { home: '首页', projects: '项目', recycle: '回收站', skills: 'Skills' }[section]
          return <div key={section} className="mf-home__tab-item" data-selected={snapshot.section === section}>
            <div role="tab" tabIndex={0} className="mf-home__tab" title={label} aria-selected={snapshot.section === section} onClick={() => selectTab(section)} onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                selectTab(section)
              }
            }}><span className="mf-home__tab-label">{label}</span></div>
            {tabSweep?.section === section && <span key={tabSweep.sequence} className="mf-home__tab-mirror" aria-hidden="true" onAnimationEnd={() => setTabSweep((current) => current?.sequence === tabSweep.sequence ? null : current)} />}
            <button type="button" className="mf-home__tab-close" aria-label={`关闭 ${label}`} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); controller.closeSection(section) }}>
              <svg width="13" height="13" viewBox="0 0 1024 1024" fill="currentColor" style={{ display: 'block', flexShrink: 0 }} aria-hidden="true" focusable="false">
                <path d="M512 466.944l233.472-233.472a31.744 31.744 0 0 1 45.056 45.056L557.056 512l233.472 233.472a31.744 31.744 0 0 1-45.056 45.056L512 557.056l-233.472 233.472a31.744 31.744 0 0 1-45.056-45.056L466.944 512 233.472 278.528a31.744 31.744 0 0 1 45.056-45.056z" />
              </svg>
            </button>
          </div>
        })}
      </div>
      </div> : null}

      <div className="mf-home__content">
        {snapshot.section !== 'projects' && !showingRecycle && (snapshot.status === 'loading' || snapshot.modelStatus === 'loading') ? <section className="mf-home__hero" data-mf-home-hero-skeleton={true} aria-label="首页能力加载中" aria-busy="true">
          <div className="mf-home__hero-skeleton-copy" aria-hidden="true" />
          <div className="mf-home__capabilities" aria-hidden="true">
            {Array.from({ length: 4 }, (_, index) => <div key={index} className="mf-home__capability mf-home__capability-skeleton" />)}
          </div>
          </section> : snapshot.section !== 'projects' && !showingRecycle ? <><section className="mf-home__hero" aria-labelledby="mf-home-title">
          <div className="mf-home__hero-copy">
            <div className="mf-home__plasma" aria-hidden="true"><Plasma color="#d6cdde" speed={0.6} direction="forward" scale={1.1} opacity={0.2} mouseInteractive={false} renderScale={0.55} maxDpr={1.5} targetFps={30} iterations={60} /></div>
            <div className="mf-home__hero-foreground">
              <h1 id="mf-home-title">只需一张画布<br />连接你的多种创意想法</h1>
              {canCreate ? <MediaButton variant="primary" type="button" className="mf-home__primary" disabled={busy} onClick={openCreate}><PlusIcon />新建画布</MediaButton> : null}
            </div>
          </div>
          <div className="mf-home__capabilities" aria-label="AI媒体能力">
            {capabilities.map((item) => <article key={item.modality} className="mf-home__capability" data-tone={item.tone} data-mf-model-key={item.modelKey} tabIndex={0} aria-label={`${item.title}：${item.copy}，逐步开放中...`}>
              <span className="mf-home__capability-status">逐步开放中...</span>
              <div className="mf-home__capability-panel" aria-hidden="true" />
              <div className="mf-home__capability-copy"><strong><CapabilityIcon name={item.icon} />{item.title}</strong><span className="mf-home__capability-description" title={item.copy}>{item.copy}</span></div>
              <div className="mf-home__capability-arrow" aria-hidden="true"><svg width="20" height="20" viewBox="64 64 896 896" fill="currentColor" focusable="false"><path d={CAPABILITY_ICON_PATHS.ArrowRight[0]} /></svg></div>
            </article>)}
          </div>
        </section>
          {snapshot.modelError ? <div className="mf-home__error" role="status"><code>{snapshot.modelError.code}</code>{snapshot.modelError.message}{snapshot.modelError.request_id ? <small>　request_id: {snapshot.modelError.request_id}</small> : null}</div> : null}
        </> : null}

        {snapshot.section === 'skills' ? <section className="mf-home__projects">{skillsContent}</section> : showingRecycle || snapshot.section === 'projects' ? <section className="mf-home__projects" aria-labelledby="mf-project-heading" data-mf-recycle={showingRecycle}>
          <div className="mf-home__projects-head">
            <div>
              <h2 id="mf-project-heading">{showingRecycle ? '回收站' : '全部项目'}</h2>
              {!showingRecycle ? <div className="mf-home__filters">
                <MediaSelect aria-label="项目状态" value={snapshot.filters.status} onChange={(value) => settleMediaHomeEvent(() => controller.setFilters({ status: value }))} options={[{ value: 'active', label: '进行中' }, { value: 'archived', label: '已归档' }]} />
                <MediaSelect<MediaProjectScope | ''> aria-label="项目归属" value={snapshot.filters.scope ?? ''} onChange={(value) => settleMediaHomeEvent(() => controller.setFilters({ scope: value || undefined }))} options={[{ value: '', label: '全部归属' }, { value: 'owner', label: '我的项目' }, { value: 'group', label: '小组项目' }, { value: 'team', label: '团队项目' }]} />
                <MediaSelect<MediaProjectOwnerFilter> aria-label="项目创建者" value={snapshot.filters.owner} onChange={(value) => settleMediaHomeEvent(() => controller.setFilters({ owner: value }))} options={[{ value: 'all', label: '全部成员' }, { value: 'mine', label: '我创建的' }]} />
              </div> : null}
            </div>
            {!showingRecycle ? <MediaInput className="mf-home__search" style={{ width: '100%' }} type="search" aria-label="搜索项目" placeholder="搜索项目名称" value={snapshot.searchInput} onChange={(event: ChangeEvent<HTMLInputElement>) => controller.setSearchInput(event.target.value)} /> : null}
          </div>

          {snapshot.actionError ? <div className="mf-home__error" role="alert"><code>{snapshot.actionError.code}</code>{snapshot.actionError.message}{snapshot.actionError.request_id ? <small>　request_id: {snapshot.actionError.request_id}</small> : null}<MediaButton type="button" aria-label="关闭操作错误" onClick={() => controller.dismissActionError()}>×</MediaButton></div> : null}

          <div className="mf-home__grid">
            {!showingRecycle && canCreate ? <article className="mf-project-card mf-project-card--create" data-mf-create-canvas={true}>
              <button type="button" className="mf-project-card__cover mf-project-card__create" aria-label="开始创作，新建画布" disabled={busy} onClick={openCreate}>
                <svg className="mf-home__primary-icon" width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true" focusable="false">
                  <path d="M32 11V53M11 32H53" stroke="currentColor" strokeWidth={2 + 64 / 35} strokeLinecap="round" />
                </svg>
                <strong>开始创作</strong>
              </button>
              <div className="mf-project-card__body"><div className="mf-project-card__title-row"><strong>创建新的画布</strong></div></div>
            </article> : null}
          {snapshot.status === 'loading' ? <div className="mf-home__loading-items" data-mf-home-skeleton={true}>{Array.from({ length: 8 }, (_, index) => <div key={index} className="mf-home__skeleton" />)}</div> : null}
          {snapshot.status === 'error' && snapshot.error ? <div className="mf-home__state" role="alert"><div><strong><code>{snapshot.error.code}</code> {snapshot.error.message}</strong>{snapshot.error.request_id ? <span>request_id: {snapshot.error.request_id}</span> : null}<br /><MediaButton type="button" onClick={() => settleMediaHomeEvent(() => controller.retry())}>重试</MediaButton></div></div> : null}
          {snapshot.status === 'ready' && visibleProjects.length === 0 && (showingRecycle || !canCreate || snapshot.query) ? <div className="mf-home__state"><div><strong>{showingRecycle ? '回收站为空' : snapshot.query ? '没有匹配的项目' : canCreate ? '创建第一张画布' : '暂无可查看的项目'}</strong><span>{showingRecycle ? '当前权限下没有可恢复项目；已删除内容由服务端按 30 天规则处理。' : snapshot.query ? '清除搜索或调整筛选条件' : canCreate ? '从文本、图像、视频和音频节点开始创作' : '请联系团队管理员开通项目创建权限'}</span><br />{!showingRecycle && (snapshot.query || canCreate) ? <MediaButton type="button" onClick={snapshot.query ? () => controller.setSearchInput('') : openCreate}>{snapshot.query ? '清除搜索' : '新建画布'}</MediaButton> : null}</div></div> : null}
          {snapshot.status === 'ready' && visibleProjects.length > 0 ? <>
            {showingRecycle ? snapshot.recycleItems.map((project) => <article key={project.uuid} className="mf-project-card" data-project-uuid={project.uuid}>
              <div className="mf-project-card__cover">{coverFor(project.cover && Date.parse(project.cover.expires_at) > Date.now() ? { kind: project.cover.kind, url: project.cover.access_url } : undefined)}</div>
              <div className="mf-project-card__body">
                <strong title={project.name}>{project.name}</strong>
                <div className="mf-project-card__meta"><span>{scopeLabel(project.scope)}</span><time dateTime={project.deleted_at}>删除于 {dateLabel(project.deleted_at, '删除时间未知')}</time></div>
                <div className="mf-project-card__meta"><span>{remainingTimeLabel(project.remaining_seconds)}</span><time dateTime={project.expires_at}>到期 {dateLabel(project.expires_at, '到期时间未知')}</time></div>
                <div className="mf-project-card__actions">
                  {canDelete ? <MediaButton type="button" disabled={busy} onClick={() => settleMediaHomeEvent(() => controller.restoreProject(project.uuid))}>恢复</MediaButton> : null}
                  {canDelete && project.can_purge ? <MediaButton type="button" disabled={busy} onClick={() => setDialog({ kind: 'purge', project })}>提前清理</MediaButton> : null}
                  {snapshot.purgeRequestedProjectUuids.includes(project.uuid) ? <span role="status">已提交清理请求</span> : null}
                </div>
              </div>
            </article>) : snapshot.items.map((project) => {
              const menuOpen = openProjectMenuUuid === project.uuid
              const closeMenu = () => setOpenProjectMenuUuid((current) => current === project.uuid ? undefined : current)
              const runMenuAction = (operation: () => void) => {
                closeMenu()
                operation()
              }
              return <article key={project.uuid} className="mf-project-card" data-project-uuid={project.uuid}>
                <button type="button" className="mf-project-card__cover" aria-label={`打开画布：${project.name}`} disabled={busy} onClick={() => runMenuAction(() => controller.openProject(project.uuid))}>
                  {coverFor(snapshot.covers[project.uuid])}
                </button>
                <div className="mf-project-card__body">
                  <div className="mf-project-card__title-row">
                    <strong title={project.name}>{project.name}</strong>
                    <MediaPopover
                      open={menuOpen}
                      onOpenChange={(open) => setOpenProjectMenuUuid((current) => open ? project.uuid : current === project.uuid ? undefined : current)}
                      trigger={['hover', 'click']}
                      placement="bottomRight"
                      arrow={false}
                      align={{ offset: [0, 4] }}
                      classNames={{ root: 'media-ui-overlay mf-project-menu-overlay' }}
                      styles={{ container: { padding: 0, background: 'transparent', boxShadow: 'none' } }}
                      content={<ProjectCardMenu
                        project={project}
                        busy={busy}
                        canCreate={canCreate}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        onOpen={() => runMenuAction(() => controller.openProject(project.uuid))}
                        onRename={() => runMenuAction(() => setDialog({ kind: 'rename', project, name: project.name }))}
                        onEditCover={() => runMenuAction(() => requestCoverUpload(project))}
                        onDuplicate={() => runMenuAction(() => settleMediaHomeEvent(() => controller.duplicateProject(project.uuid, `${Array.from(project.name).slice(0, 77).join('')} 副本`)))}
                        onExpand={(target) => runMenuAction(() => setDialog({ kind: 'expand', project, targetScope: target }))}
                        onArchive={() => runMenuAction(() => settleMediaHomeEvent(() => controller.archiveProject(project.uuid)))}
                        onRestore={() => runMenuAction(() => settleMediaHomeEvent(() => controller.restoreProject(project.uuid)))}
                        onDelete={() => runMenuAction(() => setDialog({ kind: 'delete', project }))}
                      />}
                    >
                      <button type="button" className="mf-project-card__more" aria-label={`${project.name} 项目操作`} aria-haspopup="menu" aria-expanded={menuOpen} disabled={busy}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true" focusable="false">
                          <circle cx="4" cy="9" r="1" />
                          <circle cx="9" cy="9" r="1" />
                          <circle cx="14" cy="9" r="1" />
                        </svg>
                      </button>
                    </MediaPopover>
                  </div>
                  <div className="mf-project-card__meta"><span>{scopeLabel(project.scope)} · 创建者 {project.owner_user_id}</span><time dateTime={project.updated_at}>{dateLabel(project.updated_at)}</time></div>
                  <div className="mf-project-card__meta"><span>{project.node_count} 个节点</span><span>{project.task_count} 个任务</span></div>
                </div>
              </article>
            })}
          </> : null}
          </div>

          {snapshot.status === 'ready' && snapshot.total > snapshot.pageSize ? <nav className="mf-home__pagination" aria-label={showingRecycle ? '回收站分页' : '项目分页'}><MediaButton type="button" disabled={snapshot.page <= 1} onClick={() => settleMediaHomeEvent(() => controller.setPage(snapshot.page - 1))}>上一页</MediaButton><span>第 {snapshot.page} 页</span><MediaButton type="button" disabled={snapshot.page * snapshot.pageSize >= snapshot.total} onClick={() => settleMediaHomeEvent(() => controller.setPage(snapshot.page + 1))}>下一页</MediaButton></nav> : null}
        </section> : null}
      </div>
    </section>

    {dialog ? <MediaModal open title={dialog.kind === 'create' ? '新建画布' : dialog.kind === 'rename' ? '重命名项目' : dialog.kind === 'delete' ? '删除项目' : dialog.kind === 'expand' ? '扩大项目可见范围' : '提前清理项目'} width={430} onCancel={() => { if (!busy) setDialog(undefined) }} keyboard={!busy} mask={{ closable: !busy }} closable={{ disabled: busy, 'aria-label': '关闭' }}>
      <form className="mf-home__dialog-form" onSubmit={(event) => { event.preventDefault(); if (!busy && ((dialog.kind !== 'create' && dialog.kind !== 'rename') || (dialog.name.trim().length > 0 && Array.from(dialog.name.trim()).length <= 80))) settleMediaHomeEvent(submitDialog) }}>
      {snapshot.actionError ? <p role="alert"><code>{snapshot.actionError.code}</code> {snapshot.actionError.message}</p> : null}
      {dialog.kind === 'delete' ? <p>项目将进入回收站，服务端会保留 30 天。确定删除“{dialog.project.name}”吗？</p> : null}
      {dialog.kind === 'expand' ? <p>将“{dialog.project.name}”从{scopeLabel(dialog.project.scope)}扩大到{dialog.targetScope === 'group' ? '小组项目' : '团队项目'}。此操作不可缩小，确定继续吗？</p> : null}
      {dialog.kind === 'purge' ? <p>这会提交异步清理请求，项目和媒体不会在此刻被宣称为已物理删除。确定提交“{dialog.project.name}”的清理请求吗？</p> : null}
      {dialog.kind === 'create' || dialog.kind === 'rename' ? <label>项目名称<MediaInput autoFocus disabled={busy} value={dialog.name} maxLength={80} style={{ width: '100%' }} onChange={(event) => setDialog(dialog.kind === 'create' ? { ...dialog, name: event.target.value } : { ...dialog, name: event.target.value })} /></label> : null}
      {dialog.kind === 'create' ? <label htmlFor="mf-create-project-scope">项目归属<MediaSelect<MediaProjectScope> id="mf-create-project-scope" aria-label="新建项目归属" disabled={busy} value={dialog.scope} onChange={(value) => setDialog({ ...dialog, scope: value })} options={[{ value: 'owner', label: '仅自己' }, { value: 'group', label: '所属小组' }, { value: 'team', label: '当前团队' }]} /></label> : null}
      <div className="mf-home__dialog-actions"><MediaButton type="button" disabled={busy} onClick={() => setDialog(undefined)}>取消</MediaButton><MediaButton variant={dialog.kind === 'delete' || dialog.kind === 'purge' ? 'danger' : 'primary'} loading={busy} type="submit" disabled={busy || ((dialog.kind === 'create' || dialog.kind === 'rename') && (dialog.name.trim().length === 0 || Array.from(dialog.name.trim()).length > 80))}>{dialog.kind === 'delete' ? '确认删除' : dialog.kind === 'expand' ? '确认扩大范围' : dialog.kind === 'purge' ? '提交清理请求' : '确认'}</MediaButton></div>
    </form></MediaModal> : null}
  </main>
}
